import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { ThemeProvider } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import React from "react";
import type { JSX } from "react";
import { Cookies } from "react-cookie";

import {
    bodyStyle,
    linkStyle,
    menuListStyle,
    profileAvatarStyle,
    profileBtnStyle,
    profileIdStyle,
    profileInfoStyle,
    titleStyle,
} from "./stylesheet";

import { AppContext, ApplicationContext } from "../../../ApplicationContext";
import { useCurrentUser } from "../../../hooks";
import AuthRequest from "../../../models/AuthRequest";
import WeaveBrandTheme from "../../../themes/WeaveBrandTheme";

export default function ProfileFlyout(): JSX.Element {
    const { data: currentUser } = useCurrentUser();
    const fullName = `${currentUser?.given_name ?? ""} ${currentUser?.family_name ?? ""}`;

    const appContext = React.useContext<AppContext>(ApplicationContext);

    const [logoutLink, setLogoutLink] = React.useState<string>("");

    const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);

    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    // Run on load
    React.useEffect(() => {
        let isStillMounted = true;
        (async () => {
            const auth = new AuthRequest();
            const logoutUrl = await auth.logoutUrl();
            if (isStillMounted) {
                setLogoutLink(logoutUrl);
            }
        })();

        return () => {
            isStillMounted = false;
        };
    }, []);

    // Set cookie for Feedback plugin
    React.useEffect(() => {
        const cookies = new Cookies();
        const sso = cookies.get("identity-sso");
        if (currentUser?.sub && !sso) {
            cookies.set("identity-sso", currentUser.sub, {
                secure: appContext.env.REACT_APP_ENV !== "local",
                sameSite: appContext.env.REACT_APP_ENV === "local" ? "none" : "lax",
            });
        }
    }, [appContext.env.REACT_APP_ENV, currentUser?.sub]);

    return (
        <>
            <IconButton onClick={handleOpenUserMenu} sx={profileBtnStyle}>
                <Avatar alt={fullName} sx={profileAvatarStyle}>
                    {fullName?.charAt(0).toUpperCase()}
                </Avatar>
            </IconButton>
            <ThemeProvider theme={WeaveBrandTheme.dark}>
                <Menu
                    sx={{ mt: "35px" }}
                    id="menu-appbar-profile"
                    anchorEl={anchorElUser}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    keepMounted
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                    slotProps={{ list: menuListStyle }}
                >
                    <Typography component="li" sx={profileInfoStyle}>
                        <Typography data-testid="fsr-profile-fullname" component="span" sx={titleStyle}>
                            {fullName}
                        </Typography>
                        <Typography data-testid="fsr-profile-email" component="span" sx={bodyStyle}>
                            {currentUser?.email}
                        </Typography>
                        {currentUser?.sub && (
                            <Typography component="span" sx={profileIdStyle}>
                                User ID: <span>{currentUser.sub}</span>
                            </Typography>
                        )}
                    </Typography>
                    <MenuItem sx={{ padding: "8px 16px", borderWidth: 0 }}>
                        <Link data-testid="fsr-profile-logout" sx={linkStyle} href={logoutLink}>
                            <Typography component="span">Logout</Typography>
                        </Link>
                    </MenuItem>
                </Menu>
            </ThemeProvider>
        </>
    );
}
